// Fuel & Loss panel — surfaces the refinery's OWN fuel/flare/loss accounting (read straight from the
// customer workbook by massBalanceFuelLoss.ts). The stock-movement reconciliation elsewhere closes to ~0
// and does NOT represent physical loss, which is why this panel exists. Handles both dataset formats:
//   • OAS/EET  — the "MTD" master sheet (Own/Total Fuel, Unaccounted Loss, Flare, BS&W, yields, monthly trend)
//   • AORA/Toledo — the Volume/Mass "Loss/Gain" line (Total Charges − Total Yields), volume + mass basis.
import React, { useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Chip, Table, TableBody, TableCell, TableHead, TableRow,
  Tooltip, Divider, Alert, Stack, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend,
  ReferenceLine,
} from 'recharts';
import { FuelLossReport } from '../utils/massBalanceFuelLoss';
import { LossSeries } from '../utils/massBalanceStore';

const n0 = (v: number | null | undefined) => (v == null ? '—' : Math.round(v).toLocaleString());
const n1 = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString(undefined, { maximumFractionDigits: 1 }));
const pctOf = (frac: number | null | undefined) => (frac == null ? '—' : `${(frac * 100).toFixed(2)}%`);

const KPI: React.FC<{ label: string; value: string; sub?: string; icon?: React.ReactNode; tone?: 'loss' | 'fuel' | 'neutral'; tip?: string }> =
({ label, value, sub, icon, tone = 'neutral', tip }) => {
  const border = tone === 'loss' ? '#c62828' : tone === 'fuel' ? '#e08600' : 'divider';
  const body = (
    <Card variant="outlined" sx={{ height: '100%', borderTop: 3, borderTopColor: border }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          {icon}
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{label}</Typography>
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  );
  return tip ? <Tooltip title={tip}>{body}</Tooltip> : body;
};

type TrendItem = 'fuel' | 'flare' | 'bsw' | 'unacc';
// Item colours match the stacked bars. The two overlay LINES were both orange (Loss % #e08600, Natural
// Gas #e0a85c) against an orange fuel bar -- "super hard to differentiate" (Leslie, 15 Oct). Now purple
// and teal: neither is used by any bar, the rolling average (green) or the ±2σ lines (grey).
const ITEMS: Record<TrendItem, { name: string; color: string }> = {
  fuel: { name: 'Fuel (incl. NG)', color: '#c07a1e' },
  flare: { name: 'Flare', color: '#c62828' },
  bsw: { name: 'BS&W', color: '#1976d2' },
  unacc: { name: 'Unaccounted Loss', color: '#9e9e9e' },
};
const LOSS_PCT_COLOR = '#7b1fa2';
const NG_COLOR = '#00897b';
const SIGMA_COLOR = '#616161';

const FuelLossPanel: React.FC<{ report: FuelLossReport | null | undefined; series?: LossSeries | null }> = ({ report, series }) => {
  // Leslie's Loss Diagnostic: "Trending Own Fuel, Nat Gas, Flare and BS&W. In month (bars) and
  // 12 month rolling avg."
  //   * Own Fuel, Flare, BS&W and the unaccounted loss are all in the site's monthly trend table.
  //   * NATURAL GAS is NOT -- the table has no column for it, so it exists only for the period on
  //     screen (shown in the KPI row above). It is deliberately absent here rather than drawn as a
  //     zero series that would read as "no gas burned".
  // `other` used to be max(0, totalLoss - flare - bsw), which treats totalLoss as CONTAINING flare
  // and BS&W. It does not: totalLoss IS the unaccounted loss, and flare + BS&W are the accounted
  // loss alongside it. On 2026-08 that subtraction went negative (2,666 - 1,771 - 1,093) and was
  // clamped to zero, quietly hiding the whole unaccounted figure.
  const trend = useMemo(() => {
    // Prefer the backend loss series: it carries NATURAL GAS, which the site's restated trend table
    // has no column for, so only it can split fuel into Own Fuel + NG. Falls back to the report's own
    // table when the series has not loaded (or a dataset has no series).
    const fromSeries = (series?.periods || []).filter((p) => /^\d{4}-\d{2}$/.test(p.period || ''));
    const all = fromSeries.length
      ? fromSeries.map((p) => {
          const fuel = p.fuel ?? 0, ng = p.natural_gas ?? 0;
          const flare = p.flare ?? 0, bsw = p.bsw ?? 0, unacc = p.unaccounted_loss ?? 0;
          // ng is NOT added to the stack: it is already inside the site's fuel figure in most
          // months, so stacking it would double count. It rides as an overlay line instead.
          return { month: p.period.slice(2), fuel, ng, flare, bsw, unacc,
                   fnl: fuel + flare + bsw + unacc, lossPct: p.loss_pct ?? null };
        })
      : (report?.monthlyTrend || [])
        .filter((m) => /^\d{4}-\d{2}$/.test(m.month) && (m.totalLoss != null))
        .map((m) => {
          const fuel = m.fuel ?? 0, flare = m.flare ?? 0, bsw = m.bsw ?? 0, unacc = m.totalLoss ?? 0;
          return { month: m.month.slice(2), fuel, ng: 0, flare, bsw, unacc,
                   fnl: fuel + flare + bsw + unacc, lossPct: m.lossPct != null ? m.lossPct * 100 : null };
        });
    if (!all.length) return [];
    // Trailing 12-month mean needs the eleven months BEFORE the window, so average over the whole
    // series and only then cut to the last 18 for display.
    // Every trendable item carries its own rolling average, so the single-item views can show one.
    const avg = (w: typeof all, k: 'fnl' | TrendItem) => w.reduce((a, x) => a + x[k], 0) / w.length;
    return all.map((r, i) => {
      const w = all.slice(Math.max(0, i - 11), i + 1);
      return { ...r, fnlAvg: avg(w, 'fnl'), fuelAvg: avg(w, 'fuel'), flareAvg: avg(w, 'flare'),
               bswAvg: avg(w, 'bsw'), unaccAvg: avg(w, 'unacc') };
    }).slice(-18);
  }, [report, series]);

  // Leslie (CalWork, 15 Oct): "be able to choose the item to trend (BS&W or Flare or Fuel) ... keeping
  // the 12 mo rolling and overall +-2 sigma on the trend all the time." 'all' is the stacked view she
  // also wanted kept. Unaccounted is offered too since it is already on the chart. Fuel is ~58,000 MT
  // against ~1,500 for flare, so trending one item on its own axis is the only way to READ the losses.
  const [view, setView] = useState<'all' | TrendItem>('all');
  // +/-2 sigma over the months on screen -- the same method as the Mass Gain/Loss chart.
  const band = useMemo(() => {
    const k = view === 'all' ? 'fnl' : view;
    const vals = trend.map((r: any) => r[k] as number);
    if (vals.length < 2) return null;
    const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
    const sd = Math.sqrt(vals.reduce((a, b) => a + (b - mean) ** 2, 0) / vals.length);
    return { mean, upper: mean + 2 * sd, lower: Math.max(0, mean - 2 * sd) };
  }, [trend, view]);

  if (!report) return null;
  const U = report.units === 'MT' ? 'MT' : (report.basis?.mass?.unit || 'MT');

  // ---------- AORA / Toledo ----------
  if (report.source === 'aora') {
    const mass = report.basis?.mass, vol = report.basis?.volume;
    return (
      <Card variant="outlined" sx={{ mt: 2, borderColor: '#c62828', borderWidth: 1 }}>
        <CardContent>
          <Header />
          <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
            The stock reconciliation closes to ~0 by construction. Below is the production report's own
            <strong> Loss/Gain</strong> line (Total Charges − Total Yields) — the actual refinery balance.
          </Alert>
          <Grid container spacing={1.5}>
            <Grid item xs={6} md={3}><KPI label={`Loss / Gain (mass)`} value={`${n0(mass?.lossGain)} ${mass?.unit || ''}`} sub={mass?.lossGainPct != null ? `${mass.lossGainPct.toFixed(2)}% of charges` : undefined} tone="loss" icon={<TrendingDownIcon fontSize="small" color="error" />} tip="Total Yields − Total Charges on a MASS basis — the physically meaningful refinery loss/gain." /></Grid>
            <Grid item xs={6} md={3}><KPI label={`Loss / Gain (volume)`} value={`${n0(vol?.lossGain)} ${vol?.unit || ''}`} sub={vol?.lossGainPct != null ? `${vol.lossGainPct.toFixed(2)}% of charges` : undefined} tone="neutral" tip="Volume basis — expands vs mass because refining yields lighter, less-dense products." /></Grid>
            <Grid item xs={6} md={3}><KPI label="Total Charges (mass)" value={`${n0(mass?.charges)} ${mass?.unit || ''}`} /></Grid>
            <Grid item xs={6} md={3}><KPI label="Total Yields (mass)" value={`${n0(mass?.yields)} ${mass?.unit || ''}`} /></Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Source: production report Volume/Mass sheets · period {report.period}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // ---------- OAS / EET ----------
  return (
    <Card variant="outlined" sx={{ mt: 2, borderColor: '#c62828', borderWidth: 1 }}>
      <CardContent>
        <Header />
        <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
          The stock reconciliation closes to ~0. These are the site's own Fuel &amp; Loss figures from the
          monthly balance (MTD) sheet — fuel burned, flare, BS&amp;W (together the <em>accounted</em> loss)
          and the <em>unaccounted</em> loss, against crude throughput.
        </Alert>

        {/* Headline KPIs */}
        <Grid container spacing={1.5}>
          <Grid item xs={6} md={2.4}><KPI label="Crude Processed" value={`${n0(report.crudeProcessed)}`} sub={U} tip="Crude + HSGO processed this period (MT)." /></Grid>
          <Grid item xs={6} md={2.4}><KPI label="Total Fuel" value={`${n0(report.totalFuel)}`} sub={report.fuelPct != null ? `${pctOf(report.fuelPct)} of crude` : U} tone="fuel" icon={<LocalGasStationIcon fontSize="small" sx={{ color: '#e08600' }} />} tip="Refinery own fuel + natural gas consumed (MT). This is real consumption, not a stock loss." /></Grid>
          <Grid item xs={6} md={2.4}><KPI label="Unaccounted Loss" value={`${n0(report.unaccountedLoss)}`} sub={report.lossPct != null ? `${pctOf(report.lossPct)} of crude` : U} tone="loss" icon={<TrendingDownIcon fontSize="small" color="error" />} tip="The refinery's unaccounted loss — the number the utility previously showed as 0." /></Grid>
          <Grid item xs={6} md={2.4}><KPI label="Flare" value={`${n1(report.flare)}`} sub={U} icon={<LocalFireDepartmentIcon fontSize="small" sx={{ color: '#c62828' }} />} tip="Hydrocarbon flared this period (MT), from the monthly loss breakdown." /></Grid>
          <Grid item xs={6} md={2.4}><KPI label="BS&W" value={`${n1(report.bsw)}`} sub={U} icon={<WaterDropIcon fontSize="small" color="primary" />} tip="Basic sediment & water removed (MT)." /></Grid>
        </Grid>

        {/* Disposition of crude — where the mass went, summing to 100% of crude */}
        {(() => {
          const crude = report.crudeProcessed || 0;
          if (crude <= 0) return null;
          const fuel = report.totalFuel || 0, flare = report.flare || 0, bsw = report.bsw || 0, loss = report.unaccountedLoss || 0;
          // crude = products + fuel + ACCOUNTED loss (flare + BS&W) + UNACCOUNTED loss.
          // This previously read `products = crude - fuel - loss` and then drew flare and BS&W as
          // separate segments on top, so they were counted twice: EET 2026-08 summed to 860,553 MT
          // against 860,355 MT of crude, over by exactly flare + BS&W - unaccounted (198 MT).
          const products = Math.max(0, crude - fuel - flare - bsw - loss);
          const segs = [
            { label: 'Products & other outputs', mt: products, color: '#2f7d4f' },
            { label: 'Own fuel + NG', mt: fuel, color: '#c07a1e' },
            { label: 'Flare', mt: flare, color: '#c0362c' },
            { label: 'BS&W', mt: bsw, color: '#2e6e9e' },
            { label: 'Unaccounted Loss', mt: loss, color: '#9e9e9e' },
          ].filter((s) => s.mt > 0);
          const pct = (mt: number) => (mt / crude) * 100;
          return (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Where the crude went — mass basis (sums to 100% of {n0(crude)} {U} crude)</Typography>
              <Box sx={{ display: 'flex', width: '100%', height: 26, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                {segs.map((s, i) => (
                  <Tooltip key={i} title={`${s.label}: ${n0(s.mt)} ${U} (${pct(s.mt).toFixed(2)}%)`}>
                    <Box sx={{ width: `${pct(s.mt)}%`, bgcolor: s.color, minWidth: pct(s.mt) > 0 ? 2 : 0 }} />
                  </Tooltip>
                ))}
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
                {segs.map((s, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: s.color }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {s.label} — <strong>{pct(s.mt).toFixed(2)}%</strong> ({n0(s.mt)} {U})
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          );
        })()}

        {/* Own Fuel, split by what was actually burned. Only the condensed MT export publishes this,
            so it is simply absent for periods issued in the older layout rather than shown empty. */}
        {(report.fuelComponents?.length ?? 0) > 0 && (() => {
          const comps = report.fuelComponents!;
          const sum = comps.reduce((a, c) => a + c.mass, 0);
          const own = report.ownFuel ?? sum;
          // The parts do NOT add up to the site's Own Fuel figure (57,787 vs 58,370 on 2026-09).
          // Say so rather than quietly rescaling to 100% and implying a reconciliation.
          const gap = own - sum;
          const COLORS = ['#c07a1e', '#8d6e63', '#a1887f', '#bcaaa4'];
          return (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                What the fuel was — Own Fuel by stream ({U})
              </Typography>
              <Box sx={{ display: 'flex', width: '100%', height: 26, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                {comps.map((c, i) => (
                  <Tooltip key={c.sap} title={`${c.name}: ${n0(c.mass)} ${U} (${((c.mass / sum) * 100).toFixed(1)}% of fuel burned)`}>
                    <Box sx={{ width: `${(c.mass / sum) * 100}%`, bgcolor: COLORS[i % COLORS.length] }} />
                  </Tooltip>
                ))}
              </Box>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                {comps.map((c, i) => (
                  <Typography key={c.sap} variant="caption" color="text.secondary">
                    <Box component="span" sx={{ display: 'inline-block', width: 9, height: 9, borderRadius: '2px', bgcolor: COLORS[i % COLORS.length], mr: 0.5 }} />
                    {c.name} — <strong>{((c.mass / sum) * 100).toFixed(1)}%</strong> ({n0(c.mass)} {U})
                  </Typography>
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                These streams are made in the refinery and burned, so they do not appear in the Feed balance —
                that table covers what was brought IN, including the imported natural gas.
                {Math.abs(gap) > 1 && <> The streams total {n0(sum)} {U} against an Own Fuel figure of {n0(own)} {U};
                  <strong> {n0(Math.abs(gap))} {U} is not attributed to a stream</strong>.</>}
              </Typography>
            </Box>
          );
        })()}

        {/* Monthly loss-decomposition trend */}
        {trend.length > 1 && (() => {
          const single = view !== 'all';
          const showNg = view === 'all' || view === 'fuel';
          const showLossPct = view === 'all' || view === 'unacc';
          const avgKey = single ? `${view}Avg` : 'fnlAvg';
          const avgName = single ? `12-mo rolling avg (${ITEMS[view as TrendItem].name})` : '12-mo rolling avg (F&L)';
          const fmtMt = (v: number) => `${Math.round(v).toLocaleString()} ${U}`;
          const title = single ? `${ITEMS[view as TrendItem].name} by month` : 'Fuels & Losses by month — Fuel / Flare / BS&W / Unaccounted';
          return (
          <Box sx={{ mt: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              <Typography variant="subtitle2">{title} ({U})</Typography>
              <Box sx={{ flex: 1 }} />
              <ToggleButtonGroup size="small" exclusive value={view} onChange={(_e, v) => v && setView(v)}>
                <ToggleButton value="all" sx={{ py: 0.1, px: 1.2, fontSize: '0.7rem' }}>All</ToggleButton>
                {(Object.keys(ITEMS) as TrendItem[]).map((k) => (
                  <ToggleButton key={k} value={k} sx={{ py: 0.1, px: 1.2, fontSize: '0.7rem' }}>
                    {k === 'fuel' ? 'Fuel' : k === 'unacc' ? 'Unaccounted' : ITEMS[k].name}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Stack>
            <Box sx={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <ComposedChart data={trend} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="mt" tick={{ fontSize: 11 }}
                    tickFormatter={(v) => (Math.abs(v) >= 10000 ? `${Math.round(v / 1000)}k` : Number(v).toLocaleString())} />
                  {showLossPct && <YAxis yAxisId="pct" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />}
                  <RTooltip formatter={(v: any, k: any) => (k === 'Loss %' ? `${Number(v).toFixed(2)}%` : fmtMt(Number(v)))} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {single ? (
                    <Bar yAxisId="mt" dataKey={view} name={ITEMS[view as TrendItem].name} fill={ITEMS[view as TrendItem].color} />
                  ) : (
                    (Object.keys(ITEMS) as TrendItem[]).map((k) => (
                      /* Fuel dwarfs the losses (~58,000 vs ~1,500 MT) and that IS the picture in the
                         stacked view: fuel is roughly 90% of Fuels & Losses. */
                      <Bar key={k} yAxisId="mt" dataKey={k} name={ITEMS[k].name} stackId="a" fill={ITEMS[k].color} />
                    ))
                  )}
                  {/* ±2σ of whatever is being trended -- always on, per Leslie. ifOverflow extends the
                      axis so a band above the tallest bar is still drawn rather than silently clipped. */}
                  {band && [
                    { y: band.upper, lab: `+2σ ${fmtMt(band.upper)}`, pos: 'insideTopLeft' },
                    { y: band.lower, lab: `−2σ ${fmtMt(band.lower)}`, pos: 'insideBottomLeft' },
                  ].map((r) => (
                    <ReferenceLine key={r.pos} yAxisId="mt" y={r.y} stroke={SIGMA_COLOR} strokeWidth={1.5}
                      strokeDasharray="4 3" ifOverflow="extendDomain"
                      label={{ value: r.lab, position: r.pos as any, style: { fontSize: 11, fontWeight: 600, fill: SIGMA_COLOR } }} />
                  ))}
                  <Line yAxisId="mt" dataKey={avgKey} name={avgName} stroke="#1b5e20" strokeWidth={2} dot={false} strokeDasharray="5 3" />
                  {/* Natural gas sits INSIDE the fuel bar, so it is drawn as a line rather than a
                      stacked band -- stacking it would count it twice. */}
                  {showNg && <Line yAxisId="mt" dataKey="ng" name="of which Natural Gas" stroke={NG_COLOR} strokeWidth={2} dot={false} />}
                  {showLossPct && <Line yAxisId="pct" dataKey="lossPct" name="Loss %" stroke={LOSS_PCT_COLOR} strokeWidth={2} dot={false} />}
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {single
                ? <>Bars are each month&apos;s {ITEMS[view as TrendItem].name.toLowerCase()}; the dashed green line is its trailing 12-month average.</>
                : <>Bars are the month; the dashed green line is the trailing 12-month average of Fuels &amp; Losses.</>}
              {' '}Grey dashed lines are ±2σ around the mean of the {trend.length} months shown
              {band ? ` (${fmtMt(band.lower)} to ${fmtMt(band.upper)})` : ''}.
              {showNg && <> <strong>Natural Gas is already inside the Fuel bar</strong> — the teal line shows how much of
              it is imported gas, read from each month&apos;s own report, since the site&apos;s restated trend table has
              no column for it.</>}
              {showLossPct && <> The purple line is the unaccounted loss as a % of crude (right axis).</>}
              {' '}Everything else comes from the restated trend table, which is the authoritative one.
            </Typography>
          </Box>
          );
        })()}

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {/* Feed balance */}
          {report.feed.length > 0 && (
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Feed balance ({U})</Typography>
              <MiniTable
                head={['Feed', 'Receipt', 'Consumption', 'Closing']}
                rows={report.feed.map((r) => [r.name, n0(r.receipt), n0(r.consumption), n0(r.closing)])}
                align={[0, 1, 1, 1]}
              />
            </Grid>
          )}
          {/* Product yields */}
          {report.products.length > 0 && (
            <Grid item xs={12} md={7}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Product yields ({U})</Typography>
              <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
                <MiniTable
                  head={['Product', 'Production', 'Yield %']}
                  rows={report.products
                    .filter((r) => (r.production ?? 0) !== 0 || (r.yieldPct ?? 0) !== 0)
                    .map((r) => [r.name, n0(r.production), r.yieldPct != null ? `${r.yieldPct.toFixed(2)}%` : '—'])}
                  align={[0, 1, 1]}
                />
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Sub-balances */}
        {report.subBalances.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Fenceline / tank sub-balances (MTD, {U})</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {report.subBalances.map((s, i) => (
                <Chip key={i} size="small" variant="outlined"
                  label={`${s.label}: ${s.mtd != null && s.mtd >= 0 ? '+' : ''}${n0(s.mtd)}`}
                  color={s.mtd != null && Math.abs(s.mtd) > 0.5 ? (s.mtd < 0 ? 'error' : 'default') : 'default'} />
              ))}
            </Box>
          </>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Source: Daily Yields workbook — MTD sheet · period {report.period}
        </Typography>
      </CardContent>
    </Card>
  );
};

const Header: React.FC = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
    <LocalFireDepartmentIcon sx={{ color: '#c62828' }} />
    <Typography variant="h6" sx={{ fontWeight: 700 }}>Fuel &amp; Loss</Typography>
    <Chip size="small" label="from the site's own report" variant="outlined" />
    <Tooltip title="These figures come from the site's MTD sheet, which is kept in tonnes. They do not follow the m³ / MT toggle above.">
      <Chip size="small" label="always MT" color="default" variant="outlined" />
    </Tooltip>
  </Box>
);

const MiniTable: React.FC<{ head: string[]; rows: (string | number)[][]; align: number[] }> = ({ head, rows, align }) => (
  <Table size="small" sx={{ '& td, & th': { py: 0.4, px: 1, fontSize: 12.5 } }}>
    <TableHead>
      <TableRow>{head.map((h, i) => <TableCell key={i} align={align[i] ? 'right' : 'left'} sx={{ fontWeight: 700 }}>{h}</TableCell>)}</TableRow>
    </TableHead>
    <TableBody>
      {rows.map((r, ri) => (
        <TableRow key={ri} hover>
          {r.map((c, ci) => <TableCell key={ci} align={align[ci] ? 'right' : 'left'} sx={{ fontVariantNumeric: 'tabular-nums' }}>{c}</TableCell>)}
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

export default FuelLossPanel;
